import React, { useEffect, useLayoutEffect, useState, useCallback, useRef } from "react";
import Spinner from "react-bootstrap/Spinner";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { FaMagic, FaDownload, FaImage, FaPen, FaTrash, FaShareAlt, FaCopy, FaFacebook, FaCalendarAlt } from "react-icons/fa";
import { toPng } from "html-to-image";
import { handleTokenAndPostRequest } from "../utils/utils";
import AgentBadge from "../components/AgentBadge";
import EmptyFeatureState from "../components/EmptyFeatureState";
import { useViewScope } from "../contexts/ViewScopeContext";

const SIZE_CONFIG = {
  square:    { w: 1080, h: 1080, label: "Square",    sub: "1080 × 1080" },
  landscape: { w: 1200, h: 675,  label: "Landscape", sub: "1200 × 675"  },
};

export default function DigitalAds() {
  const [slug, setSlug] = useState("");
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Infographic modal state
  const [infraModalOpen, setInfraModalOpen] = useState(false);
  const [infraPrompt, setInfraPrompt] = useState("");
  const [infraSize, setInfraSize] = useState("square");
  const [infraLinkUrl, setInfraLinkUrl] = useState("");
  const [infraGenerating, setInfraGenerating] = useState(false);
  const [infraHtml, setInfraHtml] = useState(null);
  const [infraDownloading, setInfraDownloading] = useState(false);
  const [infraProgress, setInfraProgress] = useState(0);
  const infraProgressTimer = useRef(null);
  const infraPreviewRef = useRef(null);
  const infraContainerRef = useRef(null);
  const infraStreamRef = useRef(null);
  const infraStreamHtml = useRef('');
  const [infraContainerWidth, setInfraContainerWidth] = useState(0);
  const [infraContentHeight, setInfraContentHeight] = useState(null);
  const [infraId, setInfraId] = useState(null);

  // Branding, data & market type
  const [infraBranding, setInfraBranding] = useState("agent"); // "agent" | "agency"
  const [infraIncludeSnapshot, setInfraIncludeSnapshot] = useState(true);
  const [infraIncludeTrends, setInfraIncludeTrends] = useState(false);
  const [infraIncludeListings, setInfraIncludeListings] = useState(false);
  const [infraMarketType, setInfraMarketType] = useState("sale"); // "sale" | "rent"

  // Suburb multi-select state
  const [suburbQuery, setSuburbQuery] = useState("");
  const [suburbResults, setSuburbResults] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [suburbSearching, setSuburbSearching] = useState(false);
  const [suburbDropdownOpen, setSuburbDropdownOpen] = useState(false);
  const suburbSearchTimer = useRef(null);
  const suburbInputRef = useRef(null);

  // Saved infographics list state
  const [infographics, setInfographics] = useState([]);
  const [infographicsLoading, setInfographicsLoading] = useState(true);
  const [deleteInfraModal, setDeleteInfraModal] = useState({ open: false, id: null });
  const [deletingInfra, setDeletingInfra] = useState(false);

  // Social blurb modal state
  const [socialModal, setSocialModal] = useState({ open: false, infra: null });
  const [socialText, setSocialText] = useState("");
  const [socialPrompt, setSocialPrompt] = useState("");
  const [socialLoading, setSocialLoading] = useState(false);
  const [socialCopyFlash, setSocialCopyFlash] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "" });
  const socialTextRef = useRef(null);
  const socialPromptRef = useRef(null);
  const socialInfraRef = useRef(null);

  const defaultInfraBlurbPrompt = `You are a social media copywriter for a real estate agent. Your job is to write a short, shareable social media caption for an infographic the agent has created about real estate market data.

Rules:
- The caption should be 3-5 short lines max.
- Open with a punchy, attention-grabbing line about the local property market. Use an emoji.
- Reference specific market insights (e.g. "prices up 12%", "median now $850k") if the context mentions them.
- Add a line encouraging people to save/share the infographic.
- End with a short call-to-action (e.g. "DM me for a free appraisal" or "Follow for more market updates"). Use an emoji like 📩 or 👇.
- Tone: confident, conversational, helpful. Not salesy.
- Do NOT use hashtags or em dashes.
- Return only the caption text, no explanation or markdown formatting.`;

  // Auto-resize the social prompt textarea
  useEffect(() => {
    const el = socialPromptRef.current;
    if (el) {
      el.style.height = "auto";
      el.style.height = el.scrollHeight + "px";
    }
  }, [socialPrompt]);

  // Auto-resize the social caption textarea to fit its content
  useLayoutEffect(() => {
    const el = socialTextRef.current;
    if (el) {
      el.style.height = "0px";
      el.style.height = Math.max(200, el.scrollHeight) + "px";
    }
  }, [socialText, socialLoading]);

  // Facebook state
  const [fbConnected, setFbConnected] = useState(false);
  const [fbPages, setFbPages] = useState([]);
  const [fbSelectedPage, setFbSelectedPage] = useState("");
  const [fbPosted, setFbPosted] = useState([]);
  const [fbPosting, setFbPosting] = useState(false);
  const [fbPostMsg, setFbPostMsg] = useState("");
  const [infraFbPosting, setInfraFbPosting] = useState(false);
  const [infraFbMsg, setInfraFbMsg] = useState("");

  // Duplicate state
  const [duplicatingInfra, setDuplicatingInfra] = useState({});

  // Scheduled posts state
  const [scheduledPosts, setScheduledPosts] = useState([]);

  // Schedule state
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("09:00");
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [showScheduleFields, setShowScheduleFields] = useState(false);

  // Agency scope (from central context)
  const { viewScope, agentFilter, mySlug } = useViewScope();

  // Check Facebook connection status
  useEffect(() => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const email = (user?.agent_details?.agent_email || "").toLowerCase();
      if (email !== "bayan@vendorleads.com.au") return;
    } catch { return; }
    handleTokenAndPostRequest(`${window.api_host}/facebook-status`)
      .then((res) => {
        if (res.data?.connected) {
          setFbConnected(true);
          setFbPages(res.data.pages || []);
          if (res.data.pages?.length) setFbSelectedPage(res.data.pages[0].page_id);
          setFbPosted(res.data.posted || []);
        }
      })
      .catch(() => {});
    handleTokenAndPostRequest(`${window.api_host}/scheduled-posts`)
      .then((res) => {
        if (res.data?.posts) setScheduledPosts(res.data.posts.filter(p => p.status === "pending"));
      })
      .catch(() => {});
  }, []);

  async function handleSocialBlurb(infra, existingScheduled) {
    socialInfraRef.current = infra;
    setSocialModal({ open: true, infra });
    setSocialText("");
    setSocialPrompt(defaultInfraBlurbPrompt);
    setSocialLoading(true);
    setFbPostMsg("");
    if (existingScheduled) {
      setShowScheduleFields(true);
      const dt = new Date(existingScheduled.scheduled_at);
      setScheduleDate(dt.toISOString().split('T')[0]);
      setScheduleTime(dt.toTimeString().slice(0, 5));
    } else {
      setShowScheduleFields(false);
      setScheduleDate("");
      setScheduleTime("09:00");
    }
    if (existingScheduled) {
      setSocialText(existingScheduled.message || "");
      setSocialLoading(false);
      return;
    }
    try {
      const jwt_token = localStorage.getItem("jwt_token");
      const headers = { 'Content-Type': 'application/json' };
      if (jwt_token) headers['Authorization'] = `Bearer ${jwt_token}`;
      const resp = await fetch(`${window.api_host}/infographic-ai-blurb`, {
        method: 'POST', headers,
        body: JSON.stringify({ infographic_id: infra.id, blurb_prompt: defaultInfraBlurbPrompt, infographic_html: infra.html || "", stream: true }),
      });
      if (!resp.ok) { setSocialText("Failed to generate caption."); setSocialLoading(false); return; }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = '', accumulated = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const events = buf.split('\n\n');
        buf = events.pop();
        for (const event of events) {
          const dataLine = event.split('\n').find(l => l.startsWith('data: '));
          if (!dataLine) continue;
          try {
            const parsed = JSON.parse(dataLine.slice(6));
            if (parsed.type === 'chunk') { accumulated += parsed.content; setSocialText(accumulated); }
          } catch {}
        }
      }
      if (!accumulated) setSocialText("Failed to generate caption.");
    } catch {
      setSocialText("Failed to generate caption.");
    }
    setSocialLoading(false);
  }

  async function handleRegenerateBlurb() {
    const infra = socialInfraRef.current;
    if (!infra) return;
    setSocialText("");
    setSocialLoading(true);
    try {
      const jwt_token = localStorage.getItem("jwt_token");
      const headers = { 'Content-Type': 'application/json' };
      if (jwt_token) headers['Authorization'] = `Bearer ${jwt_token}`;
      const resp = await fetch(`${window.api_host}/infographic-ai-blurb`, {
        method: 'POST', headers,
        body: JSON.stringify({ infographic_id: infra.id, blurb_prompt: socialPrompt, infographic_html: infra.html || "", stream: true }),
      });
      if (!resp.ok) { setSocialText("Failed to generate caption."); setSocialLoading(false); return; }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = '', accumulated = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const events = buf.split('\n\n');
        buf = events.pop();
        for (const event of events) {
          const dataLine = event.split('\n').find(l => l.startsWith('data: '));
          if (!dataLine) continue;
          try {
            const parsed = JSON.parse(dataLine.slice(6));
            if (parsed.type === 'chunk') { accumulated += parsed.content; setSocialText(accumulated); }
          } catch {}
        }
      }
      if (!accumulated) setSocialText("Failed to generate caption.");
    } catch {
      setSocialText("Failed to generate caption.");
    }
    setSocialLoading(false);
  }

  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth < 768);
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Track the preview container width so we can scale the iframe correctly
  useEffect(() => {
    const el = infraContainerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      for (const entry of entries) {
        setInfraContainerWidth(entry.contentRect.width);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  });

  useEffect(() => {
    setLoading(true);
    handleTokenAndPostRequest(`${window.api_host}/agent-onboarding-info`, {})
      .then(res => {
        if (res.data && res.data.landing_page_slug) {
          setSlug(res.data.landing_page_slug);
          setInfraLinkUrl(`https://www.homeprice.site/property-search?agent=${res.data.landing_page_slug}`);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  // Fetch saved infographics
  const fetchInfographics = useCallback(async () => {
    setInfographicsLoading(true);
    try {
      const jwt_token = localStorage.getItem('jwt_token');
      const storedParamToken = localStorage.getItem('param_token');
      const scopeParams = `&view_scope=${encodeURIComponent(viewScope)}${agentFilter ? `&agent_filter=${encodeURIComponent(agentFilter)}` : ''}`;
      const res = await fetch(
        `${window.api_host}/infographics?param_token=${encodeURIComponent(storedParamToken || '')}&source=${encodeURIComponent(window.location.href)}${scopeParams}`,
        { headers: { 'Authorization': `Bearer ${jwt_token}` } }
      );
      const data = await res.json();
      setInfographics(Array.isArray(data.infographics) ? data.infographics : []);
    } catch (e) {
      console.error('Failed to fetch infographics:', e);
      setInfographics([]);
    }
    setInfographicsLoading(false);
  }, [viewScope, agentFilter]);

  useEffect(() => {
    fetchInfographics();
  }, [fetchInfographics]);
  const searchSuburbs = useCallback((query) => {
    if (suburbSearchTimer.current) clearTimeout(suburbSearchTimer.current);
    if (!query.trim()) {
      setSuburbResults([]);
      setSuburbDropdownOpen(false);
      return;
    }
    suburbSearchTimer.current = setTimeout(async () => {
      setSuburbSearching(true);
      try {
        const res = await fetch(
          `${window.api_host}/location-search?q=${encodeURIComponent(query)}`
        );
        const data = await res.json();
        const filtered = (Array.isArray(data) ? data : []).filter(
          item => !selectedLocations.some(s => s.value === item.value && s.type === item.type)
        );
        setSuburbResults(filtered);
        setSuburbDropdownOpen(filtered.length > 0);
      } catch (e) {
        setSuburbResults([]);
      }
      setSuburbSearching(false);
    }, 300);
  }, [selectedLocations]);

  function formatLocationLabel(item) {
    if (item.type === "postcode") {
      return `Postcode ${item.value}${item.state ? `, ${item.state}` : ""}`;
    }
    return item.label || item.value;
  }

  function handleSelectLocation(item) {
    if (selectedLocations.some(s => s.value === item.value && s.type === item.type)) return;
    setSelectedLocations(prev => [...prev, item]);
    setSuburbQuery("");
    const remaining = suburbResults.filter(r => !(r.value === item.value && r.type === item.type));
    setSuburbResults(remaining);
    if (remaining.length === 0) {
      setSuburbDropdownOpen(false);
    }
    if (suburbInputRef.current) suburbInputRef.current.focus();
  }

  function handleRemoveLocation(idx) {
    setSelectedLocations(prev => prev.filter((_, i) => i !== idx));
  }

  // Generate infographic — streams HTML via SSE for real-time preview
  const handleGenerateInfographic = async () => {
    setInfraGenerating(true);
    setInfraHtml(null);
    infraStreamHtml.current = '';
    if (infraStreamRef.current) infraStreamRef.current.innerHTML = '';
    // Start progress bar — fills to ~95% over 60s then slows down
    setInfraProgress(0);
    const startTime = Date.now();
    const DURATION = 60000;
    if (infraProgressTimer.current) clearInterval(infraProgressTimer.current);
    infraProgressTimer.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const ratio = Math.min(elapsed / DURATION, 1);
      const progress = Math.min(95, ratio * 100 * (2 - ratio));
      setInfraProgress(progress);
    }, 200);
    try {
      const suburbIds = selectedLocations.map(loc => loc.value);
      const body = {
        prompt: infraPrompt,
        suburb_ids: suburbIds,
        size: infraSize,
        link_url: infraLinkUrl || undefined,
        branding: infraBranding,
        include_snapshot: infraIncludeSnapshot,
        include_trends: infraIncludeTrends,
        include_listings: infraIncludeListings,
        market_type: infraMarketType,
        stream: true,
      };
      if (infraHtml) body.existing_html = infraHtml;
      if (infraId) body.infographic_id = infraId;

      const jwt_token = localStorage.getItem("jwt_token");
      const headers = { 'Content-Type': 'application/json' };
      if (jwt_token) headers['Authorization'] = `Bearer ${jwt_token}`;

      const resp = await fetch(`${window.api_host}/generate-infographic`, {
        method: 'POST', headers, body: JSON.stringify(body),
      });

      if (!resp.ok) throw new Error("Request failed");

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const events = buf.split('\n\n');
        buf = events.pop();
        for (const event of events) {
          const dataLine = event.split('\n').find(l => l.startsWith('data: '));
          if (!dataLine) continue;
          try {
            const parsed = JSON.parse(dataLine.slice(6));
            if (parsed.type === 'chunk') {
              infraStreamHtml.current += parsed.content;
              if (infraStreamRef.current) {
                infraStreamRef.current.innerHTML = infraStreamHtml.current;
              }
            } else if (parsed.type === 'done') {
              setInfraContentHeight(null);
              setInfraHtml(parsed.html);
              if (parsed.infographic_id) setInfraId(parsed.infographic_id);
              setInfraPrompt("");
            } else if (parsed.type === 'error') {
              throw new Error(parsed.message);
            }
          } catch (parseErr) {
            if (parseErr.message !== "Request failed") console.warn('SSE parse error:', parseErr);
          }
        }
      }
    } catch (err) {
      console.error("Infographic generation error:", err);
      alert("Failed to generate infographic. Please try again.");
    }
    if (infraProgressTimer.current) clearInterval(infraProgressTimer.current);
    setInfraProgress(100);
    setTimeout(() => setInfraProgress(0), 500);
    setInfraGenerating(false);
    fetchInfographics();
  };

  // Extract usable content from possibly-full HTML document.
  // If the BE returns <!DOCTYPE html><html>..., we need to pull out <style> tags
  // and body content so they survive innerHTML injection into a <div>.
  function prepareHtmlForCapture(rawHtml) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(rawHtml, "text/html");
    // Collect all <style> tags from <head> and <body>
    const styles = Array.from(doc.querySelectorAll("style"))
      .map(s => s.outerHTML)
      .join("\n");
    // Get body content
    const bodyContent = doc.body ? doc.body.innerHTML : rawHtml;
    return styles + bodyContent;
  }

  // Convert all <img> with external src to inline base64 data URIs so html-to-image
  // doesn't need to re-fetch them (which fails due to CORS).
  async function inlineExternalImages(container) {
    const imgs = container.querySelectorAll("img[src]");
    const promises = Array.from(imgs).map(async (img) => {
      const src = img.getAttribute("src");
      if (!src || src.startsWith("data:")) return; // already inline
      try {
        const response = await fetch(src, { mode: "cors" });
        const blob = await response.blob();
        const dataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
        img.setAttribute("src", dataUrl);
      } catch {
        // CORS blocked — draw via an Image + canvas as fallback
        try {
          const dataUrl = await new Promise((resolve, reject) => {
            const image = new Image();
            image.crossOrigin = "anonymous";
            image.onload = () => {
              const canvas = document.createElement("canvas");
              canvas.width = image.naturalWidth;
              canvas.height = image.naturalHeight;
              canvas.getContext("2d").drawImage(image, 0, 0);
              resolve(canvas.toDataURL("image/png"));
            };
            image.onerror = reject;
            image.src = src;
          });
          img.setAttribute("src", dataUrl);
        } catch {
          // Can't convert — remove image so it doesn't break the export
          img.removeAttribute("src");
          img.style.display = "none";
        }
      }
    });
    await Promise.all(promises);
  }

  // 1×1 transparent PNG as fallback for any image html-to-image still can't embed
  const TRANSPARENT_PIXEL = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAC0lEQVQIHWNgAAIABQABNjN9GQAAAAlwSFlzAAAWJQAAFiUBSVIk8AAAAA0lEQVQI12NgYPgPAAEDAQAIicLsAAAAASUVORK5CYII=";

  /** Render infographic HTML to a base64 PNG string (no data: prefix). */
  async function renderInfraToBase64(html, size) {
    const w = SIZE_CONFIG[size]?.w || 1080;
    const h = SIZE_CONFIG[size]?.h || 1080;
    const iframe = document.createElement("iframe");
    iframe.style.cssText = `position:fixed;left:-99999px;top:0;width:${w}px;height:${h}px;border:none;z-index:-1;`;
    document.body.appendChild(iframe);
    try {
      const iDoc = iframe.contentDocument || iframe.contentWindow.document;
      iDoc.open();
      iDoc.write(html);
      iDoc.close();
      await new Promise(r => setTimeout(r, 800));
      await inlineExternalImages(iDoc.body);
      await new Promise(r => setTimeout(r, 300));
      const captureRoot = iDoc.body.firstElementChild || iDoc.body;
      const actualH = Math.max(h, iDoc.documentElement.scrollHeight || h);
      captureRoot.style.width = w + "px";
      captureRoot.style.height = actualH + "px";
      captureRoot.style.overflow = "hidden";
      iframe.style.height = actualH + "px";
      const dataUrl = await toPng(captureRoot, {
        width: w,
        height: actualH,
        pixelRatio: 2,
        cacheBust: true,
        skipAutoScale: true,
        imagePlaceholder: TRANSPARENT_PIXEL,
      });
      return dataUrl.split(",")[1]; // return pure base64
    } finally {
      document.body.removeChild(iframe);
    }
  }

  const handleInfraDownload = async () => {
    if (!infraHtml) return;
    setInfraDownloading(true);
    const w = SIZE_CONFIG[infraSize].w;
    const h = SIZE_CONFIG[infraSize].h;

    // Use an offscreen iframe (not a div) so the full HTML document structure
    // (<html>, <body>, CSS selectors, media queries, viewport units) is preserved
    // — matching the preview rendering exactly and preventing layout differences.
    const iframe = document.createElement("iframe");
    iframe.style.cssText = `position:fixed;left:-99999px;top:0;width:${w}px;height:${h}px;border:none;z-index:-1;`;
    document.body.appendChild(iframe);

    try {
      const iDoc = iframe.contentDocument || iframe.contentWindow.document;
      iDoc.open();
      iDoc.write(infraHtml);
      iDoc.close();

      // Wait for the iframe to fully render
      await new Promise(r => setTimeout(r, 800));

      // Convert external images to inline data URIs before capture
      await inlineExternalImages(iDoc.body);

      // Give the browser frames to layout + paint after inlining images
      await new Promise(r => setTimeout(r, 300));

      // Capture the body's first element child (the main wrapper) or body itself
      const captureRoot = iDoc.body.firstElementChild || iDoc.body;
      // Use actual content height if it exceeds the configured size (prevents clipping)
      const actualH = Math.max(h, iDoc.documentElement.scrollHeight || h);
      captureRoot.style.width = w + "px";
      captureRoot.style.height = actualH + "px";
      captureRoot.style.overflow = "hidden";
      // Also resize the iframe itself so toPng sees the full area
      iframe.style.height = actualH + "px";

      const png = await toPng(captureRoot, {
        width: w,
        height: actualH,
        pixelRatio: 2,
        cacheBust: true,
        skipAutoScale: true,
        imagePlaceholder: TRANSPARENT_PIXEL,
      });
      const link = document.createElement("a");
      link.href = png;
      link.download = `infographic_${infraSize}_${slug || "social"}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Save infographic image to backend
      if (infraId) {
        try {
          const base64 = png.split(",")[1];
          await handleTokenAndPostRequest(
            `${window.api_host}/save-infographic-image`,
            { infographic_id: infraId, image_base64: base64, image_mime_type: "image/png" }
          );
        } catch (saveErr) {
          console.error("Failed to save infographic image:", saveErr);
        }
      }
    } catch (err) {
      console.error("PNG export error:", err);
      // Fallback: open in new tab so user can right-click → Save Image
      try {
        const fallbackWindow = window.open("", "_blank");
        fallbackWindow.document.write(infraHtml);
        fallbackWindow.document.close();
        alert("PNG export failed. The infographic has been opened in a new tab — right-click the image and choose 'Save Image As' to download.");
      } catch {
        alert("Failed to export PNG. Please try again.");
      }
    }
    document.body.removeChild(iframe);
    setTimeout(() => setInfraDownloading(false), 1000);
  };

  const closeInfraModal = () => {
    setInfraModalOpen(false);
    setInfraHtml(null);
  };

  const openInfraModal = () => {
    setInfraPrompt("");
    setInfraSize("square");
    setSelectedLocations([]);
    setSuburbQuery("");
    setSuburbResults([]);
    setSuburbDropdownOpen(false);
    setInfraHtml(null);
    setInfraContentHeight(null);
    setInfraId(null);
    setInfraBranding("agent");
    setInfraIncludeSnapshot(true);
    setInfraIncludeTrends(false);
    setInfraIncludeListings(false);
    setInfraMarketType("sale");
    setInfraModalOpen(true);
  };

  // Open modal pre-populated from a saved infographic
  const openInfraFromSaved = (infra) => {
    setInfraPrompt("");
    setInfraSize(infra.size || "square");
    setSelectedLocations((infra.suburb_ids || []).map(id => ({ value: id, label: id, type: "suburb" })));
    setSuburbQuery("");
    setSuburbResults([]);
    setSuburbDropdownOpen(false);
    setInfraHtml(infra.html || null);
    setInfraContentHeight(null);
    setInfraId(infra.id || null);
    setInfraBranding(infra.branding || "agent");
    setInfraIncludeSnapshot(infra.include_snapshot !== false);
    setInfraIncludeTrends(infra.include_trends === true);
    setInfraIncludeListings(infra.include_listings === true);
    setInfraMarketType(infra.market_type || "sale");
    setInfraModalOpen(true);
  };

  const handleDeleteInfra = async (id) => {
    setDeletingInfra(true);
    try {
      await handleTokenAndPostRequest(
        `${window.api_host}/delete-infographic`,
        { infographic_id: id }
      );
      setInfographics(prev => prev.filter(i => i.id !== id));
      setDeleteInfraModal({ open: false, id: null });
    } catch (e) {
      alert("Failed to delete infographic.");
    }
    setDeletingInfra(false);
  };

  const handleDuplicateInfra = async (id) => {
    setDuplicatingInfra(prev => ({ ...prev, [id]: true }));
    try {
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/duplicate-infographic`,
        { infographic_id: id }
      );
      if (res.data?.status === "success") {
        await fetchInfographics();
        setToast({ show: true, message: "Infographic duplicated!" });
        setTimeout(() => setToast({ show: false, message: "" }), 3000);
      }
    } catch (e) {
      alert("Failed to duplicate infographic.");
    }
    setDuplicatingInfra(prev => ({ ...prev, [id]: false }));
  };

  const handleScheduleInfra = async () => {
    if (!scheduleDate || !scheduleTime || !socialText.trim() || !fbSelectedPage || !socialModal.infra) return;
    setScheduleSaving(true);
    try {
      // Pre-save the rendered image to the DB so the cron job can use it
      try {
        const image_base64 = await renderInfraToBase64(socialModal.infra.html, socialModal.infra.size || "square");
        if (image_base64 && socialModal.infra.id) {
          await handleTokenAndPostRequest(
            `${window.api_host}/save-infographic-image`,
            { infographic_id: socialModal.infra.id, image_base64, image_mime_type: "image/png" }
          );
        }
      } catch (e) {
        console.warn("Could not pre-save infographic image:", e);
      }
      const scheduledAt = new Date(`${scheduleDate}T${scheduleTime}:00`).toISOString();
      const infraId = String(socialModal.infra.id);
      // Check if there's an existing scheduled post for this infographic
      const existing = scheduledPosts.find(p => p.asset_type === "infographic" && p.asset_id === infraId);
      if (existing && existing.id) {
        // Update existing scheduled post
        await handleTokenAndPostRequest(
          `${window.api_host}/update-scheduled-post`,
          { id: existing.id, message: socialText, scheduled_at: scheduledAt, page_id: fbSelectedPage }
        );
        setScheduledPosts(prev => prev.map(p =>
          p.id === existing.id ? { ...p, message: socialText, scheduled_at: scheduledAt, page_id: fbSelectedPage } : p
        ));
        setToast({ show: true, message: "Schedule updated!" });
      } else {
        // Create new scheduled post
        const res = await handleTokenAndPostRequest(
          `${window.api_host}/schedule-social-post`,
          { asset_type: "infographic", asset_id: infraId, page_id: fbSelectedPage, message: socialText, scheduled_at: scheduledAt }
        );
        setScheduledPosts(prev => [...prev, { id: res.data?.id, asset_type: "infographic", asset_id: infraId, scheduled_at: scheduledAt, status: "pending", message: socialText, page_id: fbSelectedPage }]);
        setToast({ show: true, message: "Post scheduled!" });
      }
      setSocialModal({ open: false, infra: null });
      setShowScheduleFields(false);
      setScheduleDate("");
      setScheduleTime("09:00");
      setTimeout(() => setToast({ show: false, message: "" }), 3000);
    } catch (e) {
      alert("Failed to schedule post.");
    }
    setScheduleSaving(false);
  };

  return (
    <div style={{ display: "flex", height: "100vh", background: "#f6f8fa" }}>
      {/* Infographic generation modal */}
      {infraModalOpen && (
        <div
          onClick={closeInfraModal}
          style={{
            position: "fixed",
            top: 0, right: 0, bottom: 0,
            left: isMobile ? 0 : 260,
            background: "rgba(0,0,0,0.25)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 0,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: 0,
              width: "100%",
              height: "100%",
              boxShadow: "none",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            {/* Header */}
            <div style={{
              fontSize: 20, fontWeight: 700, padding: "20px 28px 12px 28px",
              borderBottom: "1px solid #e5e7eb", flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "space-between"
            }}>
              {infraHtml ? "Edit Infographic" : "Generate Infographic"}
              <button
                onClick={closeInfraModal}
                disabled={infraGenerating}
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: 22, color: "#9ca3af", padding: "0 4px", lineHeight: 1 }}
                title="Close"
              >×</button>
            </div>

            {/* Scrollable body */}
            <div style={{ flex: 1, overflowY: "auto", padding: "20px 28px", display: "flex", flexDirection: "column", gap: 16 }}>

              {/* Suburb multi-select */}
              <div style={{ position: "relative" }}>
                <label style={{ fontWeight: 600, fontSize: 14, color: "#374151", marginBottom: 2, display: "block" }}>
                  Suburbs / Postcodes
                </label>
                <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.4, marginBottom: 8 }}>
                  Choose one or more suburbs or postcodes to pull market data for. Selecting a postcode includes all suburbs within it. Multiple selections will be combined in the infographic.
                </div>
                {selectedLocations.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                    {selectedLocations.map((loc, idx) => (
                      <span
                        key={`${loc.type}-${loc.value}-${idx}`}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                          background: "#eff6ff",
                          color: "#1d4ed8",
                          border: "1px solid #bfdbfe",
                          borderRadius: 16,
                          padding: "3px 10px",
                          fontSize: 13,
                          fontWeight: 600,
                        }}
                      >
                        {formatLocationLabel(loc)}
                        <button
                          onClick={() => handleRemoveLocation(idx)}
                          disabled={infraGenerating}
                          style={{
                            background: "none", border: "none", color: "#1d4ed8",
                            cursor: "pointer", padding: 0, fontSize: 15,
                            lineHeight: 1, fontWeight: 700, marginLeft: 2,
                          }}
                        >×</button>
                      </span>
                    ))}
                  </div>
                )}
                <div style={{ position: "relative" }}>
                  <input
                    ref={suburbInputRef}
                    value={suburbQuery}
                    autoComplete="off"
                    name="suburb-search"
                    onChange={e => {
                      setSuburbQuery(e.target.value);
                      searchSuburbs(e.target.value);
                    }}
                    onFocus={() => { if (suburbResults.length > 0) setSuburbDropdownOpen(true); }}
                    onBlur={() => { setTimeout(() => { setSuburbDropdownOpen(false); setSuburbQuery(""); }, 200); }}
                    placeholder="Type a suburb or postcode…"
                    disabled={infraGenerating}
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      borderRadius: 6,
                      border: "1px solid #e5e7eb",
                      fontSize: 15,
                      boxSizing: "border-box",
                    }}
                  />
                  {suburbSearching && (
                    <div style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)" }}>
                      <Spinner animation="border" size="sm" style={{ width: 16, height: 16 }} />
                    </div>
                  )}
                </div>
                {suburbDropdownOpen && suburbResults.length > 0 && (
                  <div style={{
                    position: "absolute",
                    left: 0, right: 0,
                    top: "100%",
                    zIndex: 10,
                    background: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: 8,
                    boxShadow: "0 8px 24px #0002",
                    maxHeight: 220,
                    overflowY: "auto",
                    marginTop: 4,
                  }}>
                    {suburbResults.map((item, idx) => (
                      <div
                        key={`${item.type}-${item.value}-${idx}`}
                        onMouseDown={e => {
                          e.preventDefault();
                          handleSelectLocation(item);
                        }}
                        style={{
                          padding: "10px 14px",
                          cursor: "pointer",
                          fontSize: 14,
                          borderBottom: idx < suburbResults.length - 1 ? "1px solid #f3f4f6" : "none",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                        onMouseOver={e => e.currentTarget.style.background = "#f0f7ff"}
                        onMouseOut={e => e.currentTarget.style.background = "#fff"}
                      >
                        <span>{formatLocationLabel(item)}</span>
                        <span style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase", fontWeight: 600 }}>{item.type}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Branding toggle */}
              <div>
                <label style={{ fontWeight: 600, fontSize: 14, color: "#374151", marginBottom: 4, display: "block" }}>
                  Branding
                </label>
                <div style={{ display: "flex", gap: 8 }}>
                  {[{ value: "agent", label: "Agent" }, { value: "agency", label: "Agency" }].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setInfraBranding(opt.value)}
                      disabled={infraGenerating}
                      style={{
                        flex: 1,
                        padding: "10px 16px",
                        borderRadius: 8,
                        border: infraBranding === opt.value ? "2px solid #2563eb" : "1px solid #e5e7eb",
                        background: infraBranding === opt.value ? "#eff6ff" : "#fff",
                        color: infraBranding === opt.value ? "#2563eb" : "#374151",
                        fontWeight: 600,
                        fontSize: 14,
                        cursor: infraGenerating ? "not-allowed" : "pointer",
                        transition: "all 0.15s",
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Data selection checkboxes */}
              <div>
                <label style={{ fontWeight: 600, fontSize: 14, color: "#374151", marginBottom: 8, display: "block" }}>
                  Data to Include
                </label>
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {[
                    {
                      key: "snapshot",
                      label: "Suburb current snapshot",
                      desc: "Latest month's summary for each suburb — median sold price, days on market, median rent, and property counts. Best for a point-in-time market overview.",
                      checked: infraIncludeSnapshot,
                      setter: setInfraIncludeSnapshot,
                    },
                    {
                      key: "trends",
                      label: "Suburb monthly trends",
                      desc: "Month-by-month history of sales volume, median sold price, days on market, rental volume and median rent. Useful for showing price growth or seasonal patterns over time.",
                      checked: infraIncludeTrends,
                      setter: setInfraIncludeTrends,
                    },
                    {
                      key: "listings",
                      label: "My listings",
                      desc: "Your own active and recently sold/leased listings. Include this to promote specific properties or showcase your recent results.",
                      checked: infraIncludeListings,
                      setter: setInfraIncludeListings,
                    },
                  ].map(opt => (
                    <label
                      key={opt.key}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 8,
                        cursor: infraGenerating ? "not-allowed" : "pointer",
                        fontSize: 14,
                        color: "#374151",
                        userSelect: "none",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={opt.checked}
                        onChange={e => opt.setter(e.target.checked)}
                        disabled={infraGenerating}
                        style={{ width: 16, height: 16, accentColor: "#2563eb", cursor: infraGenerating ? "not-allowed" : "pointer", marginTop: 2, flexShrink: 0 }}
                      />
                      <span>
                        <span style={{ fontWeight: 500 }}>{opt.label}</span>
                        <span style={{ display: "block", fontSize: 12, color: "#6b7280", lineHeight: 1.4, marginTop: 1 }}>{opt.desc}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Market type selector */}
              <div>
                <label style={{ fontWeight: 600, fontSize: 14, color: "#374151", marginBottom: 2, display: "block" }}>
                  Market
                </label>
                <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.4, marginBottom: 8 }}>
                  Choose whether the infographic focuses on the sales market (median sold prices, days on market, sales volume) or the rental market (median rent, rental days on market, rental listings).
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  {[{ value: "sale", label: "Sale" }, { value: "rent", label: "Rent" }].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setInfraMarketType(opt.value)}
                      disabled={infraGenerating}
                      style={{
                        flex: 1,
                        padding: "10px 16px",
                        borderRadius: 8,
                        border: infraMarketType === opt.value ? "2px solid #2563eb" : "1px solid #e5e7eb",
                        background: infraMarketType === opt.value ? "#eff6ff" : "#fff",
                        color: infraMarketType === opt.value ? "#2563eb" : "#374151",
                        fontWeight: 600,
                        fontSize: 14,
                        cursor: infraGenerating ? "not-allowed" : "pointer",
                        transition: "all 0.15s",
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Size selector */}
              <div>
                <label style={{ fontWeight: 600, fontSize: 14, color: "#374151", marginBottom: 4, display: "block" }}>
                  Size
                </label>
                <div style={{ display: "flex", gap: 8 }}>
                  {Object.entries(SIZE_CONFIG).map(([value, cfg]) => ({ value, label: cfg.label, sub: cfg.sub })).map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setInfraSize(opt.value)}
                      disabled={infraGenerating}
                      style={{
                        flex: 1,
                        padding: "12px 16px",
                        borderRadius: 8,
                        border: infraSize === opt.value ? "2px solid #2563eb" : "1px solid #e5e7eb",
                        background: infraSize === opt.value ? "#eff6ff" : "#fff",
                        color: infraSize === opt.value ? "#2563eb" : "#374151",
                        fontWeight: 600,
                        fontSize: 14,
                        cursor: infraGenerating ? "not-allowed" : "pointer",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 2,
                        transition: "all 0.15s",
                      }}
                    >
                      {opt.label}
                      <span style={{ fontSize: 11, fontWeight: 400, color: infraSize === opt.value ? "#3b82f6" : "#9ca3af" }}>
                        {opt.sub}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Prompt */}
              <div>
                <label style={{ fontWeight: 600, fontSize: 14, color: "#374151", marginBottom: 4, display: "block" }}>
                  {infraHtml ? "Edit Instructions" : "Prompt"}
                </label>
                {!infraHtml && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                    {[
                      { label: "🏠 Market Snapshot", prompt: "Create a clean, modern infographic showing key housing market statistics including median price, days on market, auction clearance rate, and annual growth. Use large bold numbers and a professional layout." },
                      { label: "📈 Price Growth", prompt: "Design a bold dark-themed infographic highlighting 12-month property price growth with a large upward trend chart, percentage changes for houses vs units, and a comparison to the metro average." },
                      { label: "🏡 Suburb Profile", prompt: "Create a vibrant suburb profile infographic with median house and unit prices, rental yield, population stats, and a lifestyle description. Use a friendly layout suitable for Instagram with rounded elements and icons." },
                      { label: "📊 Rental Yield", prompt: "Generate a professional infographic focused on rental investment data: gross rental yield, median weekly rent for houses and units, vacancy rate, and year-on-year rent change. Use clear data visualisation." },
                      { label: "🔥 Hot Market", prompt: "Design an eye-catching \"Hot Market Alert\" infographic with fast-selling stats: average days on market, number of sales this quarter, price range, and percentage sold above asking. Use an urgent, energetic feel." },
                    ].map((example, idx) => (
                      <button
                        key={idx}
                        onClick={() => setInfraPrompt(example.prompt)}
                        disabled={infraGenerating}
                        style={{
                          background: infraPrompt === example.prompt ? "#dbeafe" : "#f3f4f6",
                          color: infraPrompt === example.prompt ? "#1d4ed8" : "#374151",
                          border: infraPrompt === example.prompt ? "1px solid #93c5fd" : "1px solid #e5e7eb",
                          borderRadius: 999,
                          padding: "5px 14px",
                          fontSize: 13,
                          fontWeight: 500,
                          cursor: infraGenerating ? "not-allowed" : "pointer",
                          whiteSpace: "nowrap",
                          transition: "all 0.15s",
                        }}
                      >
                        {example.label}
                      </button>
                    ))}
                  </div>
                )}
                <textarea
                  value={infraPrompt}
                  onChange={e => setInfraPrompt(e.target.value)}
                  disabled={infraGenerating}
                  placeholder={infraHtml
                    ? "Describe the changes you'd like — e.g. Make the title bigger, swap the background to dark, add a QR code in the bottom-right corner…"
                    : "e.g. Create a bold dark-themed infographic showing Wollongong house market stats with big numbers"}
                  rows={4}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 6,
                    border: "1px solid #e5e7eb",
                    fontSize: 15,
                    boxSizing: "border-box",
                    resize: "vertical",
                    fontFamily: "inherit",
                    lineHeight: 1.5,
                  }}
                />
              </div>

              {/* Live HTML Preview */}
              {(infraHtml || infraGenerating) && (
                <div style={{ width: isMobile ? "100%" : "50%", margin: isMobile ? undefined : "0 auto" }}>
                  <label style={{ fontWeight: 600, fontSize: 14, color: "#374151", marginBottom: 8, display: "block" }}>
                    Preview
                  </label>
                  {infraGenerating && !infraHtml ? (
                    <div
                      ref={infraContainerRef}
                      style={{
                        width: "100%",
                        position: "relative",
                        overflow: "hidden",
                        borderRadius: 8,
                        border: "1px solid #e5e7eb",
                        boxShadow: "0 2px 12px #0001",
                        background: "#f9fafb",
                        paddingBottom: `${(SIZE_CONFIG[infraSize].h / SIZE_CONFIG[infraSize].w) * 100}%`,
                      }}
                    >
                      <div
                        ref={infraStreamRef}
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: SIZE_CONFIG[infraSize].w,
                          height: SIZE_CONFIG[infraSize].h,
                          transformOrigin: "top left",
                          transform: infraContainerWidth > 0
                            ? `scale(${infraContainerWidth / SIZE_CONFIG[infraSize].w})`
                            : "scale(1)",
                          overflow: "hidden",
                          padding: 0,
                        }}
                      />
                    </div>
                  ) : (
                  <div
                    ref={infraContainerRef}
                    style={{
                      width: "100%",
                      position: "relative",
                      overflow: "hidden",
                      borderRadius: 8,
                      border: "1px solid #e5e7eb",
                      boxShadow: "0 2px 12px #0001",
                      background: "#f9fafb",
                      paddingBottom: infraContentHeight
                        ? `${(infraContentHeight / SIZE_CONFIG[infraSize].w) * 100}%`
                        : `${(SIZE_CONFIG[infraSize].h / SIZE_CONFIG[infraSize].w) * 100}%`,
                    }}
                  >
                    {infraContainerWidth > 0 && (
                      <iframe
                        ref={infraPreviewRef}
                        title="Infographic preview"
                        sandbox="allow-same-origin"
                        srcDoc={infraHtml}
                        onLoad={() => {
                          try {
                            const iframeDoc = infraPreviewRef.current?.contentDocument;
                            if (iframeDoc) {
                              // Allow a moment for fonts / images to settle
                              setTimeout(() => {
                                const h = iframeDoc.documentElement.scrollHeight || iframeDoc.body.scrollHeight;
                                if (h && h > 0) setInfraContentHeight(h);
                              }, 300);
                            }
                          } catch { /* sandbox may block */ }
                        }}
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: SIZE_CONFIG[infraSize].w,
                          height: infraContentHeight || SIZE_CONFIG[infraSize].h,
                          border: "none",
                          transformOrigin: "top left",
                          transform: `scale(${infraContainerWidth / SIZE_CONFIG[infraSize].w})`,
                          display: "block",
                        }}
                      />
                    )}
                  </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            {infraGenerating && (
              <div style={{ padding: "0 28px", flexShrink: 0 }}>
                <div style={{
                  width: "100%",
                  height: 4,
                  background: "#e5e7eb",
                  borderRadius: 2,
                  overflow: "hidden",
                }}>
                  <div style={{
                    height: "100%",
                    width: `${infraProgress}%`,
                    background: "linear-gradient(90deg, #2563eb, #3b82f6)",
                    borderRadius: 2,
                    transition: infraProgress === 100 ? "width 0.3s ease" : "width 0.4s linear",
                  }} />
                </div>
                <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4, textAlign: "center" }}>
                  {infraProgress < 95
                    ? (infraHtml ? "Applying your edits — this usually takes about a minute…" : "Generating your infographic — this usually takes about a minute…")
                    : "Almost there…"}
                </div>
              </div>
            )}
            <div style={{
              display: "flex", gap: isMobile ? 8 : 12, justifyContent: "flex-end",
              padding: "12px 28px 20px 28px", borderTop: "1px solid #e5e7eb",
              flexShrink: 0, flexWrap: "nowrap",
            }}>
              {!isMobile && (
                <button
                  onClick={closeInfraModal}
                  style={{
                    background: "#f1f5f9", color: "#374151", border: "none",
                    borderRadius: 6, padding: "8px 18px", fontWeight: 600,
                    cursor: "pointer", fontSize: 15,
                  }}
                >
                  Close
                </button>
              )}
              <button
                onClick={handleGenerateInfographic}
                disabled={infraGenerating || !infraPrompt.trim() || (!infraHtml && selectedLocations.length === 0)}
                style={{
                  background: infraGenerating ? "#93c5fd" : "#2563eb",
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  padding: isMobile ? "8px 12px" : "8px 18px",
                  fontWeight: 600,
                  cursor: (infraGenerating || !infraPrompt.trim() || (!infraHtml && selectedLocations.length === 0)) ? "not-allowed" : "pointer",
                  fontSize: isMobile ? 13 : 15,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  opacity: (!infraPrompt.trim() || (!infraHtml && selectedLocations.length === 0)) ? 0.5 : 1,
                  flex: isMobile ? 1 : undefined,
                  minWidth: 0,
                }}
              >
                {!isMobile && (infraGenerating
                  ? <Spinner animation="border" size="sm" style={{ marginRight: 8, width: 14, height: 14 }} />
                  : infraHtml
                    ? <FaPen style={{ marginRight: 8 }} />
                    : <FaMagic style={{ marginRight: 8 }} />)}
                {isMobile && infraGenerating && <Spinner animation="border" size="sm" style={{ marginRight: 6, width: 12, height: 12 }} />}
                {infraGenerating ? (infraHtml ? "Applying edits…" : "Generating…") : infraHtml ? "Apply Edits" : "Generate"}
              </button>
              {infraHtml && (
                <button
                  onClick={handleInfraDownload}
                  disabled={infraDownloading}
                  style={{
                    background: "#059669",
                    color: "#fff",
                    border: "none",
                    borderRadius: 6,
                    padding: isMobile ? "8px 12px" : "8px 18px",
                    fontWeight: 600,
                    cursor: "pointer",
                    fontSize: isMobile ? 13 : 15,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flex: isMobile ? 1 : undefined,
                    minWidth: 0,
                  }}
                >
                  {!isMobile && (infraDownloading
                    ? <Spinner animation="border" size="sm" style={{ marginRight: 8, width: 14, height: 14 }} />
                    : <FaDownload style={{ marginRight: 8 }} />)}
                  {isMobile && infraDownloading && <Spinner animation="border" size="sm" style={{ marginRight: 6, width: 12, height: 12 }} />}
                  Download PNG
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        <div
          style={{
            padding: isMobile ? "72px 16px 0 16px" : "32px 32px 0 32px",
            flex: 1,
            overflowY: "auto",
            boxSizing: "border-box",
            width: "100%",
          }}
        >
          {loading ? (
            <div style={{ maxWidth: 900, marginLeft: "auto", marginRight: "auto" }}>
              {/* Title skeleton */}
              <Skeleton width={120} height={28} style={{ marginBottom: 8 }} />
              <Skeleton width="70%" height={14} style={{ marginBottom: 32 }} />
              {/* Generator section skeleton */}
              <div style={{ background: "#fff", borderRadius: 12, padding: isMobile ? 16 : 24, boxShadow: "0 2px 8px #0001", border: "1px solid #e5e7eb", marginBottom: 24 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                  <Skeleton width={40} height={40} borderRadius={10} />
                  <div><Skeleton width={180} height={18} /><Skeleton width={280} height={13} style={{ marginTop: 4 }} /></div>
                </div>
                <Skeleton width="100%" height={40} borderRadius={8} style={{ marginBottom: 8 }} />
                <Skeleton width="100%" height={40} borderRadius={8} style={{ marginBottom: 8 }} />
                <Skeleton width={160} height={40} borderRadius={8} />
              </div>
              {/* Infographics list skeleton */}
              <Skeleton width={180} height={20} style={{ marginBottom: 16 }} />
              <div style={{ display: "flex", flexDirection: "column", gap: 16, marginBottom: 24 }}>
                {[...Array(3)].map((_, i) => (
                  <div key={i} style={{
                    background: "#fff", borderRadius: 16, overflow: "hidden",
                    boxShadow: "0 1px 4px #0001", border: "1px solid #e5e7eb",
                    display: "flex", flexDirection: isMobile ? "column" : "row",
                  }}>
                    <div style={{ width: isMobile ? "100%" : 200, minHeight: isMobile ? 160 : 140 }}>
                      <Skeleton height="100%" width="100%" borderRadius={0} style={{ display: "block", minHeight: isMobile ? 160 : 140 }} />
                    </div>
                    <div style={{ flex: 1, padding: "18px 22px", display: "flex", flexDirection: "column", gap: 8 }}>
                      <Skeleton width="65%" height={16} />
                      <Skeleton width="40%" height={12} />
                      <div style={{ display: "flex", gap: 6 }}>
                        <Skeleton width={80} height={18} borderRadius={10} />
                        <Skeleton width={100} height={18} borderRadius={10} />
                      </div>
                      <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                        <Skeleton width={70} height={28} borderRadius={8} />
                        <Skeleton width={60} height={28} borderRadius={8} />
                        <Skeleton width={60} height={28} borderRadius={8} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div style={{ maxWidth: 900, marginLeft: "auto", marginRight: "auto" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap", marginBottom: 8 }}>
                <h1 style={{ fontSize: isMobile ? 26 : 32, fontWeight: 700, color: "#1f2937", margin: 0 }}>Social</h1>
              </div>
              <p style={{ color: "#6b7280", fontSize: 15, marginBottom: 32, lineHeight: 1.6 }}>
                Create eye-catching infographics powered by real market data to share on your social media channels and drive engagement.
              </p>

              {/* Infographic section */}
              <div
                style={{
                  background: "#fff",
                  borderRadius: 12,
                  padding: isMobile ? 16 : 24,
                  boxShadow: "0 2px 8px #0001",
                  width: "100%",
                  marginBottom: 24,
                  border: "1px solid #e5e7eb",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4 }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: 10,
                    background: "#eff6ff", display: "flex",
                    alignItems: "center", justifyContent: "center",
                  }}>
                    <FaImage style={{ color: "#2563eb", fontSize: 18 }} />
                  </div>
                  <h3 style={{ fontWeight: 600, fontSize: 20, margin: 0 }}>
                    AI Infographic Generator
                  </h3>
                </div>
                <p style={{ color: "#6b7280", fontSize: 14, marginBottom: 20, marginLeft: 52, lineHeight: 1.6 }}>
                  Describe the style you want, pick your suburbs, and we'll generate a branded infographic with real market stats — ready to post on Facebook, Instagram, LinkedIn, or X.
                </p>
                <button
                  onClick={openInfraModal}
                  style={{
                    background: "#2563eb",
                    color: "#fff",
                    border: "none",
                    borderRadius: 8,
                    padding: "12px 24px",
                    fontSize: 15,
                    fontWeight: 600,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    marginLeft: 52,
                  }}
                >
                  <FaMagic />
                  Generate Infographic
                </button>
              </div>

              {/* Saved infographics list */}
              <div style={{ marginTop: 8 }}>
                <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 16 }}>Your Infographics</h2>
                {infographicsLoading ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 8, marginBottom: 24 }}>
                    {[...Array(3)].map((_, i) => (
                      <div key={i} style={{
                        background: "#fff", borderRadius: 16, overflow: "hidden",
                        boxShadow: "0 1px 4px #0001", border: "1px solid #e5e7eb",
                        display: "flex", flexDirection: isMobile ? "column" : "row",
                      }}>
                        <div style={{ width: isMobile ? "100%" : 200, minHeight: isMobile ? 160 : 140 }}>
                          <Skeleton height="100%" width="100%" borderRadius={0} style={{ display: "block", minHeight: isMobile ? 160 : 140 }} />
                        </div>
                        <div style={{ flex: 1, padding: "18px 22px", display: "flex", flexDirection: "column", gap: 8 }}>
                          <Skeleton width="65%" height={16} />
                          <Skeleton width="40%" height={12} />
                          <div style={{ display: "flex", gap: 6 }}>
                            <Skeleton width={80} height={18} borderRadius={10} />
                            <Skeleton width={100} height={18} borderRadius={10} />
                          </div>
                          <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                            <Skeleton width={70} height={28} borderRadius={8} />
                            <Skeleton width={60} height={28} borderRadius={8} />
                            <Skeleton width={60} height={28} borderRadius={8} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : infographics.length === 0 ? (
                  <EmptyFeatureState
                    icon={<FaImage />}
                    title="No Infographics Yet"
                    description="Create branded, data-driven infographics for your suburbs in seconds. Share them on social media to position yourself as the local market expert."
                    features={[
                      { icon: <FaMagic />, text: "AI generates beautiful market infographics from real suburb data" },
                      { icon: <FaShareAlt />, text: "Download as image or share directly to Facebook and Instagram" },
                      { icon: <FaCalendarAlt />, text: "Include median prices, trends, and recent listings automatically" },
                    ]}
                    examplePreview={
                      <div style={{ padding: 16 }}>
                        <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 12 }}>
                          {[
                            { suburb: "Bondi Beach", median: "$2.45M", change: "+8.2%" },
                            { suburb: "Coogee", median: "$1.85M", change: "+5.1%" },
                          ].map(s => (
                            <div key={s.suburb} style={{
                              flex: 1, background: "linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)",
                              borderRadius: 12, padding: 20, color: "#fff",
                            }}>
                              <div style={{ fontSize: 11, opacity: 0.8, marginBottom: 4 }}>Market Snapshot</div>
                              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>{s.suburb}</div>
                              <div style={{ fontSize: 22, fontWeight: 800 }}>{s.median}</div>
                              <div style={{ fontSize: 12, color: "#86efac", marginTop: 4 }}>{s.change} past 12 months</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    }
                    ctaLabel="Generate Your First Infographic"
                    onCta={() => setInfraModalOpen(true)}
                    isMobile={isMobile}
                  />
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 16, marginBottom: 24 }}>
                    {infographics.map(infra => (
                      <div
                        key={infra.id}
                        style={{
                          background: "#fff",
                          borderRadius: 16,
                          boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
                          border: "1px solid #e5e7eb",
                          padding: 0,
                          display: "flex",
                          flexDirection: isMobile ? "column" : "row",
                          position: "relative",
                          overflow: "hidden",
                          transition: "box-shadow 0.2s, transform 0.15s",
                        }}
                        onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,0.12)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
                        onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 2px 12px rgba(0,0,0,0.08)"; e.currentTarget.style.transform = "none"; }}
                      >
                        {/* HTML Preview thumbnail */}
                        {infra.html && (
                          <div
                            style={{
                              width: isMobile ? "100%" : 200,
                              height: isMobile ? 0 : "auto",
                              paddingBottom: isMobile ? `${((SIZE_CONFIG[infra.size]?.h || 1080) / (SIZE_CONFIG[infra.size]?.w || 1080)) * 100}%` : undefined,
                              minHeight: isMobile ? undefined : 160,
                              flexShrink: 0,
                              position: "relative",
                              overflow: "hidden",
                              background: "#f9fafb",
                              borderRight: isMobile ? "none" : "1px solid #e5e7eb",
                              borderBottom: isMobile ? "1px solid #e5e7eb" : "none",
                            }}
                          >
                            <iframe
                              title={`Preview ${infra.id}`}
                              sandbox="allow-same-origin"
                              srcDoc={infra.html}
                              style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: SIZE_CONFIG[infra.size]?.w || 1080,
                                height: SIZE_CONFIG[infra.size]?.h || 1080,
                                border: "none",
                                transformOrigin: "top left",
                                transform: `scale(${(isMobile ? (window.innerWidth - 34) : 200) / (SIZE_CONFIG[infra.size]?.w || 1080)})`,
                                pointerEvents: "none",
                              }}
                            />
                          </div>
                        )}

                        {/* Card content */}
                        <div style={{ flex: 1, padding: isMobile ? "16px 16px 14px" : "18px 22px", display: "flex", flexDirection: "column", minWidth: 0 }}>
                          {/* Pills row */}
                          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6, flexWrap: "wrap" }}>
                            <span style={{
                              borderRadius: 16,
                              padding: "1px 10px",
                              fontWeight: 600,
                              fontSize: 11,
                              background: "#eff6ff",
                              color: "#2563eb",
                              border: "1px solid #bfdbfe"
                            }}>
                              {SIZE_CONFIG[infra.size]?.label || infra.size || "Square"}
                            </span>
                            <span style={{
                              borderRadius: 16,
                              padding: "1px 10px",
                              fontWeight: 600,
                              fontSize: 11,
                              background: infra.market_type === "rent" ? "#fef3c7" : "#e7fbe9",
                              color: infra.market_type === "rent" ? "#b45309" : "#15803d",
                              border: `1px solid ${infra.market_type === "rent" ? "#f59e42" : "#22c55e"}`
                            }}>
                              {infra.market_type === "rent" ? "Rent" : "Sale"}
                            </span>
                            {infra.has_image && (
                              <span style={{
                                borderRadius: 16,
                                padding: "1px 10px",
                                fontWeight: 600,
                                fontSize: 11,
                                background: "#f0fdf4",
                                color: "#059669",
                                border: "1px solid #a7f3d0"
                              }}>
                                Image saved
                              </span>
                            )}
                            <AgentBadge agentName={infra.agent_name} />
                          </div>

                          {/* Prompt text */}
                          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, color: "#1a1a1a", lineHeight: 1.4, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                            {infra.prompt || "Untitled infographic"}
                          </div>

                          {/* Suburbs pills */}
                          {infra.suburb_ids && infra.suburb_ids.length > 0 && (
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 6 }}>
                              {infra.suburb_ids.slice(0, 5).map((sid, idx) => (
                                <span
                                  key={`${sid}-${idx}`}
                                  style={{
                                    background: "#f3f4f6",
                                    color: "#6b7280",
                                    border: "1px solid #e5e7eb",
                                    borderRadius: 16,
                                    padding: "1px 8px",
                                    fontSize: 11,
                                    fontWeight: 500
                                  }}
                                >
                                  {sid}
                                </span>
                              ))}
                              {infra.suburb_ids.length > 5 && (
                                <span style={{ fontSize: 11, color: "#9ca3af", alignSelf: "center" }}>
                                  +{infra.suburb_ids.length - 5} more
                                </span>
                              )}
                            </div>
                          )}

                          {/* Date + edit count */}
                          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
                            <span style={{ display: "flex", alignItems: "center", gap: 3, background: "#f3f4f6", color: "#374151", padding: "2px 10px", borderRadius: 10, fontWeight: 500, fontSize: 11 }}>
                              <FaCalendarAlt style={{ fontSize: 10 }} />
                              {infra.created_at ? new Date(infra.created_at * 1000).toLocaleDateString() : "—"}
                            </span>
                            {infra.prompt_edits && infra.prompt_edits.length > 0 && (
                              <span style={{ display: "flex", alignItems: "center", background: "#f3f4f6", color: "#374151", padding: "2px 10px", borderRadius: 10, fontWeight: 500, fontSize: 11 }}>{infra.prompt_edits.length} edit{infra.prompt_edits.length !== 1 ? "s" : ""}</span>
                            )}
                          </div>

                          {/* Action buttons */}
                          <div style={{
                            display: "flex",
                            gap: 8,
                            marginTop: 4,
                            flexWrap: "wrap",
                            alignItems: "center",
                          }}>
                            {(viewScope !== "agency" || infra.agent_slug === mySlug) && (
                            <button
                              onClick={() => openInfraFromSaved(infra)}
                              style={{
                                background: "#f3f4f6",
                                color: "#374151",
                                border: "none",
                                borderRadius: 8,
                                padding: "6px 14px",
                                fontWeight: 600,
                                cursor: "pointer",
                                fontSize: 12,
                                display: "flex",
                                alignItems: "center",
                                gap: 5,
                              }}
                            >
                              Edit
                            </button>
                            )}
                            {(() => {
                              const posted = fbPosted.some(p => p.asset_type === "infographic" && p.asset_id === String(infra.id));
                              const scheduled = scheduledPosts.find(p => p.asset_type === "infographic" && p.asset_id === String(infra.id));
                              return (
                                <>
                                  <button
                                    onClick={() => !posted && handleSocialBlurb(infra, scheduled || null)}
                                    disabled={posted}
                                    style={{
                                      background: posted ? "#d1d5db" : scheduled ? "#f5f3ff" : "#f3f4f6",
                                      color: posted ? "#6b7280" : scheduled ? "#6d28d9" : "#374151",
                                      border: scheduled && !posted ? "1px solid #ddd6fe" : "none",
                                      borderRadius: 8,
                                      padding: "6px 14px",
                                      fontWeight: 600,
                                      cursor: posted ? "not-allowed" : "pointer",
                                      fontSize: 12,
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 5,
                                    }}
                                  >
                                    <FaShareAlt style={{ fontSize: 11 }} />
                                    {posted ? "Already Posted" : scheduled ? "Edit Schedule" : "Social Post"}
                                  </button>
                                  {scheduled && (
                                    <span style={{
                                      fontSize: 11,
                                      color: "#7c3aed",
                                      fontWeight: 600,
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 4,
                                    }}>
                                      <FaCalendarAlt style={{ fontSize: 10 }} />
                                      {new Date(scheduled.scheduled_at).toLocaleDateString(undefined, { day: "numeric", month: "short" })}
                                      {" "}
                                      {new Date(scheduled.scheduled_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
                                    </span>
                                  )}
                                </>
                              );
                            })()}
                            {(viewScope !== "agency" || infra.agent_slug === mySlug) && (
                              <button
                                onClick={() => handleDuplicateInfra(infra.id)}
                                disabled={duplicatingInfra[infra.id]}
                                style={{
                                  background: "#f3f4f6",
                                  color: "#374151",
                                  border: "none",
                                  borderRadius: 8,
                                  padding: "6px 14px",
                                  fontWeight: 600,
                                  cursor: duplicatingInfra[infra.id] ? "not-allowed" : "pointer",
                                  fontSize: 12,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 5,
                                }}
                              >
                                {duplicatingInfra[infra.id]
                                  ? <Spinner animation="border" size="sm" style={{ width: 14, height: 14 }} />
                                  : <FaCopy style={{ fontSize: 11 }} />}
                                {duplicatingInfra[infra.id] ? "Duplicating..." : "Duplicate"}
                              </button>
                            )}
                            {(viewScope !== "agency" || infra.agent_slug === mySlug) && (
                            <button
                              onClick={() => setDeleteInfraModal({ open: true, id: infra.id })}
                              style={{
                                background: "transparent",
                                color: "#9ca3af",
                                border: "none",
                                borderRadius: 8,
                                padding: "6px 10px",
                                fontWeight: 600,
                                cursor: "pointer",
                                fontSize: 12,
                                display: "flex",
                                alignItems: "center",
                                gap: 5,
                                marginLeft: "auto",
                              }}
                            >
                              {deletingInfra && deleteInfraModal.id === infra.id
                                ? <Spinner animation="border" size="sm" style={{ width: 14, height: 14 }} />
                                : <FaTrash style={{ fontSize: 11 }} />}
                            </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

            </div>
          )}
        </div>
      </div>

      {/* Delete Infographic Confirmation Modal */}
      {deleteInfraModal.open && (
        <div style={{
          position: "fixed",
          top: 0, right: 0, bottom: 0,
          left: isMobile ? 0 : 260,
          background: "rgba(0,0,0,0.25)",
          zIndex: 1000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}>
          <div style={{
            background: "#fff",
            borderRadius: 10,
            padding: 32,
            minWidth: 320,
            maxWidth: 400,
            width: "90vw",
            boxShadow: "0 4px 24px #0002",
            display: "flex",
            flexDirection: "column",
            alignItems: "center"
          }}>
            <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 16 }}>Delete Infographic?</div>
            <div style={{ color: "#444", marginBottom: 24, textAlign: "center" }}>
              Are you sure you want to delete this infographic? This action cannot be undone.
            </div>
            <div style={{ display: "flex", gap: 16 }}>
              <button
                onClick={() => setDeleteInfraModal({ open: false, id: null })}
                disabled={deletingInfra}
                style={{
                  background: "#f1f5f9",
                  color: "#374151",
                  border: "none",
                  borderRadius: 6,
                  padding: "8px 18px",
                  fontWeight: 600,
                  cursor: "pointer",
                  fontSize: 15
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteInfra(deleteInfraModal.id)}
                disabled={deletingInfra}
                style={{
                  background: "#e11d48",
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  padding: "8px 18px",
                  fontWeight: 600,
                  cursor: "pointer",
                  fontSize: 15,
                  display: "flex",
                  alignItems: "center"
                }}
              >
                {!deletingInfra && <FaTrash style={{ marginRight: 8 }} />}
                {deletingInfra && <Spinner animation="border" size="sm" style={{ marginRight: 8 }} />}
                {deletingInfra ? "Deleting..." : "Yes, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Social Post Modal */}
      {socialModal.open && (
        <div style={{
          position: "fixed", top: 0, right: 0, bottom: 0,
          left: isMobile ? 0 : 260,
          background: "rgba(0,0,0,0.25)", zIndex: 1000,
          display: "flex", alignItems: "center", justifyContent: "center",
        }} onClick={() => setSocialModal({ open: false, infra: null })}>
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: "#fff", borderRadius: 0, width: "100%", height: "100%",
              boxShadow: "none",
              display: "flex", flexDirection: "column", overflow: "hidden",
            }}
          >
            <div style={{
              fontSize: 20, fontWeight: 700, padding: "20px 28px 12px 28px",
              borderBottom: "1px solid #e5e7eb", flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "space-between",
            }}>
              Social Post
              <button
                onClick={() => setSocialModal({ open: false, infra: null })}
                disabled={socialLoading}
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: 22, color: "#9ca3af", padding: "0 4px", lineHeight: 1 }}
                title="Close"
              >×</button>
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: "20px 28px", display: "flex", flexDirection: "column", gap: 16 }}>
              <div>
                <label style={{ fontWeight: 600, fontSize: 14, color: "#374151", marginBottom: 4, display: "block" }}>
                  Prompt <span style={{ fontWeight: 400, color: "#9ca3af" }}>(edit to customise the caption style)</span>
                </label>
                <textarea
                  ref={socialPromptRef}
                  value={socialPrompt}
                  onChange={e => setSocialPrompt(e.target.value)}
                  disabled={socialLoading}
                  rows={2}
                  style={{
                    width: "100%", padding: "10px 12px", borderRadius: 6,
                    border: "1px solid #e5e7eb", fontSize: 14, boxSizing: "border-box",
                    resize: "vertical", fontFamily: "inherit", lineHeight: 1.5, overflow: "hidden",
                  }}
                />
              </div>
              {socialLoading && !socialText ? (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, minHeight: 120, justifyContent: "center" }}>
                  <Spinner animation="border" />
                  <span style={{ color: "#6b7280", fontSize: 14 }}>Generating caption…</span>
                </div>
              ) : (
                <>
                  {!socialLoading && (
                  <p style={{ fontSize: 14, color: "#6b7280", margin: 0, lineHeight: 1.6 }}>
                    Copy this caption or post it directly to Facebook alongside your infographic image.
                  </p>
                  )}
                  <textarea
                    ref={socialTextRef}
                    value={socialText}
                    onChange={e => {
                      setSocialText(e.target.value);
                      e.target.style.height = "0px";
                      e.target.style.height = Math.max(200, e.target.scrollHeight) + "px";
                    }}
                    readOnly={socialLoading}
                    style={{
                      background: socialCopyFlash ? "#dbeafe" : "#f3f4f6",
                      borderRadius: 8, padding: "16px 18px", fontSize: 15, lineHeight: 1.7,
                      color: "#1f2937", width: "100%", boxSizing: "border-box",
                      border: socialCopyFlash ? "1px solid #93c5fd" : "1px solid #e5e7eb",
                      transition: "background 0.3s ease, border 0.3s ease",
                      fontFamily: "inherit", resize: "none", overflow: "hidden",
                      minHeight: 200, maxHeight: "none",
                    }}
                  />
                </>
              )}
            </div>
            {fbConnected && showScheduleFields && !socialLoading && socialText && (
              <div style={{ padding: "12px 28px 0", borderTop: "1px solid #e5e7eb" }}>
                <div style={{ display: "flex", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
                  <div style={{ flex: 1, minWidth: 140 }}>
                    <label style={{ fontWeight: 600, fontSize: 13, color: "#555", display: "block", marginBottom: 6 }}>Date</label>
                    <input
                      type="date"
                      value={scheduleDate}
                      onChange={e => setScheduleDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      style={{
                        width: "100%",
                        border: "1px solid #ddd",
                        borderRadius: 8,
                        padding: "8px 12px",
                        fontSize: 14,
                      }}
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: 140 }}>
                    <label style={{ fontWeight: 600, fontSize: 13, color: "#555", display: "block", marginBottom: 6 }}>Time</label>
                    <input
                      type="time"
                      value={scheduleTime}
                      onChange={e => setScheduleTime(e.target.value)}
                      style={{
                        width: "100%",
                        border: "1px solid #ddd",
                        borderRadius: 8,
                        padding: "8px 12px",
                        fontSize: 14,
                      }}
                    />
                  </div>
                  <div style={{ display: "flex", alignItems: "flex-end" }}>
                    <button
                      onClick={handleScheduleInfra}
                      disabled={scheduleSaving || !scheduleDate || !socialText.trim() || !fbSelectedPage}
                      style={{
                        background: (!scheduleDate || !socialText.trim()) ? "#d1d5db" : "#7c3aed",
                        color: "#fff",
                        border: "none",
                        borderRadius: 8,
                        padding: "9px 20px",
                        fontWeight: 600,
                        cursor: (!scheduleDate || !socialText.trim()) ? "not-allowed" : "pointer",
                        fontSize: 14,
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {scheduleSaving && <Spinner animation="border" size="sm" style={{ width: 14, height: 14 }} />}
                      <FaCalendarAlt />
                      {scheduleSaving ? "Scheduling..." : "Schedule Post"}
                    </button>
                  </div>
                </div>
              </div>
            )}
            <div style={{
              display: "flex", gap: 12, justifyContent: "flex-end", padding: "12px 28px 20px 28px",
              borderTop: "1px solid #e5e7eb", flexShrink: 0, flexWrap: "wrap",
            }}>
              <button
                onClick={handleRegenerateBlurb}
                disabled={socialLoading}
                style={{
                  background: socialLoading ? "#a78bfa" : "#7c3aed",
                  color: "#fff", border: "none", borderRadius: 6,
                  padding: "8px 18px", fontWeight: 600, fontSize: 15,
                  cursor: socialLoading ? "not-allowed" : "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flex: isMobile ? 1 : undefined,
                }}
              >
                {socialLoading
                  ? <Spinner animation="border" size="sm" style={{ marginRight: 8, width: 14, height: 14 }} />
                  : <FaMagic style={{ marginRight: 8 }} />}
                Regenerate
              </button>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(socialText);
                  setSocialCopyFlash(true);
                  setTimeout(() => setSocialCopyFlash(false), 600);
                  setToast({ show: true, message: "Copied to clipboard!" });
                  setTimeout(() => setToast({ show: false, message: "" }), 3000);
                }}
                disabled={socialLoading || !socialText}
                style={{
                  background: "#2563eb", color: "#fff", border: "none", borderRadius: 6,
                  padding: "8px 18px", fontWeight: 600, fontSize: 15,
                  cursor: socialLoading || !socialText ? "not-allowed" : "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  opacity: socialLoading || !socialText ? 0.5 : 1,
                  flex: isMobile ? 1 : undefined,
                }}
              >
                <FaCopy style={{ marginRight: 8 }} /> Copy
              </button>
              {fbConnected && (() => {
                const infraId = socialModal.infra?.id;
                const alreadyPosted = fbPosted.some(p => p.asset_type === "infographic" && p.asset_id === String(infraId) && p.page_id === fbSelectedPage);
                return (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flex: isMobile ? 1 : undefined }}>
                    {fbPages.length > 1 && (
                      <select
                        value={fbSelectedPage}
                        onChange={e => { setFbSelectedPage(e.target.value); setFbPostMsg(""); }}
                        style={{
                          borderRadius: 6, border: "1px solid #d1d5db", padding: "7px 8px",
                          fontSize: 13, color: "#374151", background: "#f9fafb", maxWidth: 160,
                        }}
                      >
                        {fbPages.map(p => (
                          <option key={p.page_id} value={p.page_id}>{p.page_name}</option>
                        ))}
                      </select>
                    )}
                    <button
                      onClick={async () => {
                        setFbPosting(true);
                        setFbPostMsg("");
                        try {
                          const infra = socialModal.infra;
                          let imageB64 = null;
                          if (infra?.html) {
                            try {
                              imageB64 = await renderInfraToBase64(infra.html, infra.size || "square");
                            } catch (renderErr) {
                              console.error("Failed to render infographic image:", renderErr);
                            }
                          }
                          const postData = { message: socialText, page_id: fbSelectedPage, asset_type: "infographic", asset_id: String(infraId) };
                          if (imageB64) postData.image_base64 = imageB64;
                          const res = await handleTokenAndPostRequest(
                            `${window.api_host}/facebook-post`,
                            postData,
                            { "Content-Type": "application/json" }
                          );
                          if (res.data.success) {
                            setFbPostMsg("success");
                            setFbPosted(prev => [...prev, { asset_type: "infographic", asset_id: String(infraId), page_id: fbSelectedPage }]);
                            setToast({ show: true, message: "Posted to Facebook!" });
                            setTimeout(() => setToast({ show: false, message: "" }), 3000);
                            if (imageB64 && infraId) {
                              handleTokenAndPostRequest(
                                `${window.api_host}/save-infographic-image`,
                                { infographic_id: infraId, image_base64: imageB64, image_mime_type: "image/png" }
                              ).catch(() => {});
                            }
                          } else {
                            setFbPostMsg(res.data.message || "Failed to post.");
                            if (res.data.reconnect) setFbConnected(false);
                          }
                        } catch {
                          setFbPostMsg("Failed to post to Facebook.");
                        } finally {
                          setFbPosting(false);
                        }
                      }}
                      disabled={socialLoading || !socialText || fbPosting || alreadyPosted}
                      style={{
                        background: alreadyPosted ? "#9ca3af" : fbPostMsg === "success" ? "#059669" : "#1877f2",
                        color: "#fff", border: "none", borderRadius: 6,
                        padding: "8px 18px", fontWeight: 600, fontSize: 15,
                        cursor: socialLoading || !socialText || fbPosting || alreadyPosted ? "not-allowed" : "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        opacity: socialLoading || !socialText || fbPosting ? 0.7 : 1,
                        flex: isMobile ? 1 : undefined,
                        transition: "background 0.3s",
                      }}
                    >
                      {fbPosting
                        ? <Spinner animation="border" size="sm" style={{ marginRight: 8, width: 14, height: 14 }} />
                        : <FaFacebook style={{ marginRight: 8 }} />}
                      {alreadyPosted ? "Already Posted" : fbPostMsg === "success" ? "Posted!" : "Post to Facebook"}
                    </button>
                    <button
                      onClick={() => setShowScheduleFields(v => !v)}
                      disabled={socialLoading || !socialText}
                      style={{
                        background: showScheduleFields ? "#7c3aed" : "#f3f4f6",
                        color: showScheduleFields ? "#fff" : "#374151",
                        border: "none",
                        borderRadius: 6,
                        padding: "8px 14px",
                        fontWeight: 600,
                        cursor: socialLoading || !socialText ? "not-allowed" : "pointer",
                        fontSize: 15,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        opacity: socialLoading || !socialText ? 0.5 : 1,
                        transition: "background 0.3s, color 0.3s",
                      }}
                    >
                      <FaCalendarAlt style={{ marginRight: 6 }} /> Schedule
                    </button>
                  </div>
                );
              })()}
            </div>
            {fbPostMsg && fbPostMsg !== "success" && (
              <div style={{ padding: "0 28px 12px", fontSize: 13, color: "#dc2626" }}>{fbPostMsg}</div>
            )}
          </div>
        </div>
      )}

      {/* Toast notification */}
      {toast.show && (
        <div style={{
          position: "fixed", bottom: 24,
          left: isMobile ? "50%" : "calc(50% + 130px)",
          transform: "translateX(-50%)",
          background: "#065f46", color: "#fff",
          padding: "12px 24px", borderRadius: 8,
          boxShadow: "0 2px 12px rgba(0,0,0,0.15)",
          fontSize: 14, fontWeight: 600, zIndex: 9999,
        }}>
          {toast.message}
        </div>
      )}
    </div>
  );
}